import { agaveService } from 'app/services/agave.service';
import { ERPPostingStatus, HighnoteTransactionEvent, HighnoteTransaction } from 'API';
// `AgaveGlCode` comes from the CDK-generated types because the data is fetched
// via `agaveService.v2SearchAgaveGLCodes()` (CDK AppSync, no `__typename`).
// Using the Amplify `API.AgaveGlCode` here would break typecheck against the
// runtime shape. Sibling posting files still use `API.*` for legacy paths.
import type { AgaveGlCode } from 'CDK_API';
import { PostingContext } from './types';
import { createPostingSubscription } from './sharedPostingUtils';
import { formatAmountForAgavePost, stripPaidolPrefix } from 'app/services/agave/erpUtils';
import { checkAccountingDate } from 'app/pages/overview/tabs/transactions/AccountingDate/AccountingDate';

// Sage 300 utility functions
const sage300Util = {
  speedchainGlAccountName: 'Speedchain' as const,

  createJobCostName: (transaction: HighnoteTransaction | HighnoteTransactionEvent) => {
    const jobCostName = [
      transaction.card?.last4,
      transaction.user
        ? `${transaction.user.first_name ?? ''} ${transaction.user.last_name?.substring(0, 4) ?? ''}`.trim()
        : undefined,
      transaction.merchantName,
      transaction.note,
    ]
      .filter(Boolean)
      .join('-')
      .substring(0, 30);
    return jobCostName;
  },
};

export interface Sage300APInvoicePostingParams {
  transactionId: string;
  paidolId: string;
  transaction: HighnoteTransactionEvent;
  agaveAccountToken: string;
}

/**
 * Builds the AP Invoice payload for Sage 300 posting
 */
export function buildSage300APInvoicePayload(
  transaction: HighnoteTransactionEvent,
  agaveAccountToken: string
): {
  description: string;
  due_date: string;
  issue_date: string;
  number: string;
  vendor_id: string;
  account_token: string;
  line_items: Array<{
    amount: string;
    description: string;
    quantity: string;
    cost_code_id?: string;
    cost_type_id?: string;
    project_id?: string;
    ledger_account_id?: string;
    sub_job_id?: string | null;
  }>;
} {
  const paymentNumber = transaction.id.substring(0, 15); // Use first 15 characters (max limit) to reduce collisions
  const paymentDate = new Date(transaction.createdAt).toISOString().split('T')[0];
  const { amount } = formatAmountForAgavePost(transaction.approvedAmount?.value ?? 0, transaction.status);

  return {
    description: transaction.merchantName || 'Transaction',
    due_date: paymentDate,
    issue_date: paymentDate,
    number: paymentNumber,
    vendor_id: stripPaidolPrefix(transaction.agaveVendorId) || '',
    account_token: agaveAccountToken,
    line_items: [
      {
        amount: amount,
        description: transaction.merchantName || '',
        quantity: '1.0',
        ...(transaction.agaveCostCodeId && { cost_code_id: stripPaidolPrefix(transaction.agaveCostCodeId) }),
        ...(transaction.agaveCostTypeId && { cost_type_id: stripPaidolPrefix(transaction.agaveCostTypeId) }),
        ...(transaction.agaveProjectId && { project_id: stripPaidolPrefix(transaction.agaveProjectId) }),
        ...(transaction.agaveGlCodeId && { ledger_account_id: stripPaidolPrefix(transaction.agaveGlCodeId) }),
        ...(stripPaidolPrefix(transaction.agaveSubJob?.agaveSubJobId || transaction.agaveSubJobId) && {
          sub_job_id: stripPaidolPrefix(transaction.agaveSubJob?.agaveSubJobId || transaction.agaveSubJobId),
        }),
      },
    ],
  };
}

/**
 * Handles Sage 300 AP Invoice posting via queue
 */
export async function postSage300APInvoice(
  params: Sage300APInvoicePostingParams,
  context: PostingContext
): Promise<void> {
  const { transactionId, paidolId, transaction, agaveAccountToken } = params;
  const {
    dispatch,
    enqueueSnackbar,
    t,
    setIsAgaveLoading,
    setTransactionPosting,
    captureException,
    applyLocalTransactionUpdates,
  } = context;

  setIsAgaveLoading(true);

  try {
    // Build the invoice payload
    const invoiceData = buildSage300APInvoicePayload(transaction, agaveAccountToken);

    // Set up subscription BEFORE starting the async process
    const subscription = createPostingSubscription(transactionId, context);

    // Start the async AP Invoice creation via the CDK v2-agave Lambda (SEE-308).
    // agaveService throws on transport / GraphQL errors, so we only need to
    // branch on the returned status.
    const result = await agaveService.startSage300APInvoice({
      input: {
        transactionId,
        paidolId,
        invoiceData,
      },
    });

    // Check if mutation succeeded and status is PENDING
    if (result?.status === ERPPostingStatus.PENDING) {
      enqueueSnackbar(t('submitExpense.postingInvoice'), { variant: 'info' });
    } else {
      subscription.unsubscribe();
      // If status is not PENDING, it might be FAILED - check the response
      const errorMessage = result?.error || 'Failed to start AP Invoice posting';

      // Update Redux to clear posting state immediately
      // Set erpPostingStatus to null so transaction shows as "reviewed" (not posting)
      dispatch(
        applyLocalTransactionUpdates([
          {
            id: transactionId,
            changes: {
              erpPostingStatus: null, // Clear to show as "reviewed"
              erpPostingError: errorMessage,
            },
          },
        ]) as any
      );

      throw new Error(errorMessage);
    }
  } catch (error) {
    captureException(error);
    const errorMessage = (error as Error).message || t('submitExpense.error');
    enqueueSnackbar(errorMessage, { variant: 'error' });

    // Ensure posting state is cleared and transaction status is updated
    dispatch(
      applyLocalTransactionUpdates([
        {
          id: transactionId,
          changes: {
            erpPostingStatus: null, // Clear posting status on error
            erpPostingError: errorMessage,
          },
        },
      ]) as any
    );

    setIsAgaveLoading(false);
    setTransactionPosting({ id: transactionId, isPosting: false });
  }
}

export interface Sage300JobCostPostingParams {
  transactionId: string;
  paidolId: string;
  transaction: HighnoteTransactionEvent;
  agaveAccountToken: string;
  agaveGlCode?: AgaveGlCode | null;
}

/**
 * Builds the Job Cost payload for Sage 300 posting
 */
async function buildSage300JobCostPayload(
  transaction: HighnoteTransactionEvent,
  agaveAccountToken: string,
  paidolId: string,
  agaveGlCode: AgaveGlCode | null | undefined,
  context: PostingContext
): Promise<{
  headers: string;
  data: Record<string, unknown>;
}> {
  const { t, captureException } = context;

  // Direct call to get the specific Speedchain GL code by name (SEE-308: now
  // via the CDK v2-agave Lambda, was Amplify-side `graphqlRequest`).
  let glCodesResult: Awaited<ReturnType<typeof agaveService.v2SearchAgaveGLCodes>>;
  try {
    glCodesResult = await agaveService.v2SearchAgaveGLCodes({
      input: {
        filter: {
          paidolId: paidolId,
          searchTerm: sage300Util.speedchainGlAccountName, // Filter by the specific name
          isSettingsRequest: true, // This is a settings request so we can get all the codes for posting
        },
        limit: 10,
      },
    });
  } catch (queryError) {
    captureException(queryError);
    throw new Error(t('submitExpense.error'));
  }

  const speedchainLedgerAccount = glCodesResult?.items?.find(
    (code: AgaveGlCode) => code.name === sage300Util.speedchainGlAccountName
  );

  // if we dont have the sc code, throw error:
  if (!speedchainLedgerAccount || !speedchainLedgerAccount.source_id) {
    throw new Error(t('submitExpense.speedchainGlCodeNotFound'));
  }

  const paymentDate = new Date(transaction.createdAt).toISOString().split('T')[0];
  const { amount } = formatAmountForAgavePost(transaction.approvedAmount?.value ?? 0, transaction.status);
  const jobCostName = sage300Util.createJobCostName(transaction);

  const headersJobCost: Record<string, unknown> = { 'Account-Token': agaveAccountToken };

  // Validate that transaction has required coding data
  const hasProjectCoding =
    transaction.agaveProjectId || transaction.agaveCostCodeId || transaction.agaveCostTypeId;
  const hasGLCoding = transaction.agaveGlCodeId;

  if (!hasProjectCoding && !hasGLCoding) {
    throw new Error(t('submitExpense.missingCodingData'));
  }

  const dataJobCost: Record<string, unknown> = {
    actual_amount: amount,
    name: jobCostName,
  };

  if (hasProjectCoding) {
    headersJobCost['Project-Id'] = stripPaidolPrefix(transaction.agaveProjectId);
    dataJobCost.type = 'Job Cost';
    dataJobCost.accounting_date = transaction.accountingDate
      ? checkAccountingDate(transaction.accountingDate)
      : paymentDate;
    dataJobCost.cost_code_id = stripPaidolPrefix(transaction.agaveCostCodeId);
    dataJobCost.cost_type_id = stripPaidolPrefix(transaction.agaveCostTypeId);
    dataJobCost.project_id = stripPaidolPrefix(transaction.agaveProjectId);
    dataJobCost.sub_job_id = stripPaidolPrefix(
      transaction.agaveSubJob?.agaveSubJobId || transaction.agaveSubJobId
    );
    dataJobCost.source_data = JSON.stringify({
      '0': {
        '14': speedchainLedgerAccount.source_id, // Speedchain Liability Account
      },
    });
  } else if (transaction.agaveGlCodeId) {
    // Fallback to the original GL-code based posting
    dataJobCost.type = 'General Ledger';
    dataJobCost.transaction_date = paymentDate;
    dataJobCost.accounting_date = transaction.accountingDate
      ? checkAccountingDate(transaction.accountingDate)
      : paymentDate;
    dataJobCost.source_data = JSON.stringify({
      '0': {
        '6': agaveGlCode?.source_id, // Debit Account
        '7': speedchainLedgerAccount.source_id, // Credit Account
      },
    });
  }

  return {
    headers: JSON.stringify(headersJobCost),
    data: dataJobCost,
  };
}

/**
 * Handles Sage 300 Job Cost posting via queue
 */
export async function postSage300JobCost(
  params: Sage300JobCostPostingParams,
  context: PostingContext
): Promise<void> {
  const { transactionId, paidolId, transaction, agaveAccountToken, agaveGlCode } = params;
  const {
    dispatch,
    enqueueSnackbar,
    t,
    setIsAgaveLoading,
    setTransactionPosting,
    captureException,
    applyLocalTransactionUpdates,
  } = context;

  setIsAgaveLoading(true);

  try {
    // Build the job cost payload
    const jobCostData = await buildSage300JobCostPayload(
      transaction,
      agaveAccountToken,
      paidolId,
      agaveGlCode,
      context
    );

    // Set up subscription BEFORE starting the async process
    const subscription = createPostingSubscription(transactionId, context);

    // Start the async Job Cost creation via the CDK v2-agave Lambda (SEE-308).
    // agaveService throws on transport / GraphQL errors, so we only need to
    // branch on the returned status.
    const result = await agaveService.startSage300JobCost({
      input: {
        transactionId,
        paidolId,
        jobCostData,
      },
    });

    // Check if mutation succeeded and status is PENDING
    if (result?.status === ERPPostingStatus.PENDING) {
      enqueueSnackbar(t('submitExpense.postingJobCost'), { variant: 'info' });
    } else {
      subscription.unsubscribe();
      // If status is not PENDING, it might be FAILED - check the response
      const errorMessage = result?.error || 'Failed to start Job Cost posting';

      // Update Redux to clear posting state immediately
      // Set erpPostingStatus to null so transaction shows as "reviewed" (not posting)
      dispatch(
        applyLocalTransactionUpdates([
          {
            id: transactionId,
            changes: {
              erpPostingStatus: null, // Clear to show as "reviewed"
              erpPostingError: errorMessage,
            },
          },
        ]) as any
      );

      throw new Error(errorMessage);
    }
  } catch (error) {
    captureException(error);
    const errorMessage = (error as Error).message || t('submitExpense.error');
    enqueueSnackbar(errorMessage, { variant: 'error' });

    // Ensure posting state is cleared and transaction status is updated
    dispatch(
      applyLocalTransactionUpdates([
        {
          id: transactionId,
          changes: {
            erpPostingStatus: null, // Clear posting status on error
            erpPostingError: errorMessage,
          },
        },
      ]) as any
    );

    setIsAgaveLoading(false);
    setTransactionPosting({ id: transactionId, isPosting: false });
  }
}
